import { useContext, useState } from "react";
import ToastContext from "../../context/toastProvider";
import Button from "../atoms/button";
import { createGroup, updateGroup, deleteGroup } from "./functions";
import GroupModal from "./groupModal";

const memberCountLabel = (group) => {
    const count = group.members?.length || 0;
    return count === 1 ? "1 membro" : `${count} membros`;
};

const GroupsPanel = ({ user, staff, groups, onChanged }) => {
    const { goodToast, badToast } = useContext(ToastContext);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const handleSubmit = async (body, group) => {
        const data = group
            ? await updateGroup(user.token, group._id, body)
            : await createGroup(user.token, { name: body.name, description: body.description });
        if (data.type === "error") {
            badToast(data.message);
            return;
        }
        // Criação: membros escolhidos entram num segundo passo (PUT) para manter o POST simples.
        if (!group && body.members.length && data.data?._id) {
            const memberData = await updateGroup(user.token, data.data._id, { members: body.members });
            if (memberData.type === "error") {
                // O grupo já foi criado no backend (sem membros) — não deixar o usuário achar
                // que nada foi salvo. Fecha o modal e atualiza a lista para refletir o estado
                // real; o usuário completa os membros editando o grupo em seguida.
                badToast(`Grupo criado, mas houve erro ao salvar os membros: ${memberData.message}`);
                setModalOpen(false);
                setEditing(null);
                onChanged();
                return;
            }
        }
        goodToast(group ? "Grupo atualizado" : "Grupo criado");
        setModalOpen(false);
        setEditing(null);
        onChanged();
    };

    const handleDelete = async () => {
        const data = await deleteGroup(user.token, confirmDelete._id);
        setConfirmDelete(null);
        if (data.type === "error") {
            badToast(data.message);
            return;
        }
        goodToast("Grupo removido");
        onChanged();
    };

    return (
        <div>
            <div className="mb-4 flex justify-end">
                <Button classes="bg-[#d59106] hover:bg-orange-500" click={() => { setEditing(null); setModalOpen(true); }}>
                    Criar grupo
                </Button>
            </div>
            {groups.length === 0 ? <p className="opacity-70">Nenhum grupo criado ainda.</p> : (
                <ul className="flex flex-col gap-3">
                    {groups.map((group) => (
                        <li key={group._id} className="rounded-lg border border-[#b47c06]/40 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-lg">{group.name}</p>
                                    <p className="text-sm opacity-70">{group.description}</p>
                                    <p className="text-sm"><span>{memberCountLabel(group)}</span>: {(group.members || []).map((m) => m.name).join(", ")}</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button classes="bg-gray-600 hover:bg-gray-700" click={() => { setEditing(group); setModalOpen(true); }}>
                                        Editar
                                    </Button>
                                    <Button classes="bg-red-700 hover:bg-red-800" click={() => setConfirmDelete(group)}>
                                        Excluir
                                    </Button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <GroupModal
                open={modalOpen}
                group={editing}
                staff={staff}
                onClose={() => { setModalOpen(false); setEditing(null); }}
                onSubmit={handleSubmit}
            />

            {confirmDelete ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="w-96 rounded-lg border border-[#d59106] bg-[#1c1917] p-6">
                        <p className="mb-4">Excluir o grupo "{confirmDelete.name}"?</p>
                        <div className="flex justify-end gap-3">
                            <Button classes="bg-gray-600 hover:bg-gray-700" click={() => setConfirmDelete(null)}>Cancelar</Button>
                            <Button classes="bg-red-700 hover:bg-red-800" click={handleDelete}>Excluir</Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default GroupsPanel;
